import React, { FC } from 'react';
import { Typography, Stack } from '@mui/material';
import { Button } from 'reactstrap';
import { PaymentMethod } from '@stripe/stripe-js';
import AfterpayIcon from 'src/assets/icons/afterpay.svg';
import PaymentPreview from 'src/pages/signup/steps/payment/payment-preview';
import colors from 'src/styles/equalution/colors';
import { isUserFromAustralia } from 'src/lib/utils';
import { useDecision } from '@optimizely/react-sdk';
import { isCardPaymentMethod } from '../utils/challengeAddon.util';

interface PaymentMethodSelectorProps {
  selectedPaymentType: 'card' | 'afterpay';
  onPaymentTypeChange: (type: 'card' | 'afterpay') => void;
  paymentMethod?: PaymentMethod;
  onAddCardClick: () => void;
  title?: string;
  isLoading: boolean;
}

const paymentOptionStyles = {
  base: {
    cursor: 'pointer',
    borderRadius: '12px',
    padding: '20px',
    transition: 'all 0.2s ease-in-out',
    border: '2px solid transparent',
  },
  selected: {
    backgroundColor: `${colors.SALAD}08`,
    borderColor: colors.SALAD,
    boxShadow: `0 4px 12px ${colors.SALAD}20`,
  },
  unselected: {
    backgroundColor: colors.WHITE,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
};

const radioButtonStyles = {
  base: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease-in-out',
  },
  selected: {
    borderColor: colors.SALAD,
    backgroundColor: colors.SALAD,
  },
  unselected: {
    borderColor: '#d1d5db',
    backgroundColor: 'transparent',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: colors.WHITE,
  },
};

const buttonStyles = {
  backgroundColor: (selected: boolean) =>
    selected ? colors.SALAD : 'transparent',
  borderColor: colors.SALAD,
  color: (selected: boolean) => (selected ? colors.WHITE : colors.SALAD),
  fontWeight: 600,
  minWidth: '80px',
};

export const PaymentMethodSelector: FC<PaymentMethodSelectorProps> = ({
  selectedPaymentType,
  onPaymentTypeChange,
  paymentMethod,
  onAddCardClick,
  isLoading,
  title = 'Choose Payment Method',
}) => {
  const [{ enabled: isAfterPayDisabled }] = useDecision(
    'disable_afterpay_all_flow',
    {
      autoUpdate: true,
    }
  );

  const hasCard = isCardPaymentMethod(paymentMethod);

  return (
    <Stack mt={4} mb={4}>
      <Typography
        fontSize={20}
        fontWeight={700}
        color={colors.OFF_BLACK}
        mb={3}
        textAlign="center">
        {title}
      </Typography>

      <Stack spacing={1.5}>
        <div
          style={{
            ...paymentOptionStyles.base,
            ...(selectedPaymentType === 'card'
              ? paymentOptionStyles.selected
              : paymentOptionStyles.unselected),
          }}
          onClick={() => {
            if (isLoading) {
              return;
            }
            onPaymentTypeChange('card');
          }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center">
            <Stack direction="row" alignItems="center" spacing={2}>
              <div
                style={{
                  ...radioButtonStyles.base,
                  ...(selectedPaymentType === 'card'
                    ? radioButtonStyles.selected
                    : radioButtonStyles.unselected),
                }}>
                {selectedPaymentType === 'card' && (
                  <div style={radioButtonStyles.dot} />
                )}
              </div>
              <Stack spacing={0.5}>
                <Typography
                  fontWeight={700}
                  fontSize={16}
                  color={colors.OFF_BLACK}>
                  Credit/Debit Card
                </Typography>
                <PaymentPreview
                  paymentMethod={hasCard ? paymentMethod : undefined}
                />
              </Stack>
            </Stack>
            <Button
              size="sm"
              outline={selectedPaymentType !== 'card'}
              color={selectedPaymentType === 'card' ? 'primary' : undefined}
              style={{
                backgroundColor: buttonStyles.backgroundColor(
                  selectedPaymentType === 'card'
                ),
                borderColor: buttonStyles.borderColor,
                color: buttonStyles.color(selectedPaymentType === 'card'),
                fontWeight: buttonStyles.fontWeight,
                minWidth: buttonStyles.minWidth,
              }}
              onClick={e => {
                e.stopPropagation();
                onAddCardClick();
              }}>
              {hasCard ? 'Change' : 'Add Card'}
            </Button>
          </Stack>
        </div>

        {isUserFromAustralia() && !isAfterPayDisabled && (
          <div
            style={{
              ...paymentOptionStyles.base,
              ...(selectedPaymentType === 'afterpay'
                ? paymentOptionStyles.selected
                : paymentOptionStyles.unselected),
            }}
            onClick={() => {
              if (isLoading) {
                return;
              }
              onPaymentTypeChange('afterpay');
            }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <div
                style={{
                  ...radioButtonStyles.base,
                  ...(selectedPaymentType === 'afterpay'
                    ? radioButtonStyles.selected
                    : radioButtonStyles.unselected),
                }}>
                {selectedPaymentType === 'afterpay' && (
                  <div style={radioButtonStyles.dot} />
                )}
              </div>
              <Stack direction="row" alignItems="center" spacing={2} flex={1}>
                <img
                  src={AfterpayIcon}
                  alt="Afterpay"
                  style={{ width: '44px', height: '30px' }}
                />
                <Stack>
                  <Typography
                    fontWeight={600}
                    fontSize={14}
                    color={colors.OFF_BLACK}>
                    Afterpay
                  </Typography>
                  <Typography fontSize={12} color={colors.BLUEBERRY}>
                    Pay in 4 interest-free installments
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </div>
        )}
      </Stack>
    </Stack>
  );
};
